import { designVariables } from "@studio3t/design-variables";
import { RadioButton } from "@studio3t/generic-components";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useMemo, useState } from "react";
import styled from "styled-components";
import { reservationsApi } from "@/api/client";
import { useServices } from "@/hooks/useApi";
import { type SlotIntent, slotIntent } from "@/pages/slotIntent";
import { usePipelineStaging } from "./pipelineStaging";
import {
  type SourceSlotForm,
  showsSlotSelector,
  slotFormFromIntent,
  takeoverCandidates,
} from "./sourceSlot";
import { parseResourceId } from "./stagingModel";

const FORM = designVariables.ui.form;

/**
 * How a change capture source takes its replication slot: a new one, or a
 * retained one a deleted pipeline left, continuing where it stopped. Shown
 * only for a `postgres_cdc` source; what it chooses lands in the pipeline
 * code as the source's `slot`. Adopting an existing slot is not offered
 * here, and one the pipeline code already adopts is kept until replaced.
 */
export function SourceSlotPanel() {
  const { sources, pipelineExtras, setPipelineExtras } = usePipelineStaging();
  const { data: services } = useServices();
  const source = sources[0];
  const serviceName = source
    ? parseResourceId(source.id)?.serviceName
    : undefined;
  const service = services?.find((s) => s.name === serviceName);
  const visible = showsSlotSelector(service?.provider);
  const stored = source ? pipelineExtras.slots?.[source.id] : undefined;
  const [form, setForm] = useState<SourceSlotForm>(() =>
    slotFormFromIntent(stored),
  );
  const [error, setError] = useState<string | null>(null);
  const { data: statuses } = useQuery({
    queryKey: ["reservations"],
    queryFn: reservationsApi.list,
    enabled: visible,
  });
  const candidates = useMemo(
    () => (service && statuses ? takeoverCandidates(statuses, service) : []),
    [service, statuses],
  );
  const groupName = useId();

  // A different source, or a setting read from the code, resets the form.
  useEffect(() => setForm(slotFormFromIntent(stored)), [stored]);

  if (!visible || !source) return null;

  const apply = (next: SourceSlotForm) => {
    setForm(next);
    let intent: SlotIntent | undefined;
    try {
      intent = slotIntent(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    }
    setError(null);
    const slots = { ...pipelineExtras.slots };
    if (intent) slots[source.id] = intent;
    else delete slots[source.id];
    setPipelineExtras({ ...pipelineExtras, slots });
  };

  const takeover = (id: string) => {
    const row = candidates.find((c) => c.id === id);
    if (row) {
      apply({
        mode: "takeover",
        reservation: row.id,
        expectedOwner: row.expectedOwner,
      });
    }
  };

  return (
    <Panel data-testid="source-slot-panel">
      <Title>Replication slot</Title>
      <RadioButton
        groupName={groupName}
        value="fresh"
        label="New slot"
        supportive={service?.slot}
        checked={form.mode === "fresh"}
        onEnable={() => apply({ mode: "fresh" })}
      />
      <RadioButton
        groupName={groupName}
        value="takeover"
        label="Take over a retained slot"
        supportive={
          candidates.length === 0 ? "None retained for this service" : undefined
        }
        checked={form.mode === "takeover"}
        disabled={candidates.length === 0}
        onEnable={() => candidates[0] && takeover(candidates[0].id)}
      />
      {form.mode === "takeover" && (
        <Indented>
          <select
            value={form.reservation}
            onChange={(e) => takeover(e.target.value)}
          >
            {candidates.map((c) => (
              <option key={c.id} value={c.id}>
                {c.owner} · lag {c.lag ?? "unknown"} · retained WAL{" "}
                {c.retained ?? "unknown"}
              </option>
            ))}
          </select>
          <Muted>Continues from where the deleted pipeline stopped.</Muted>
        </Indented>
      )}
      {form.mode === "adopt" && (
        <Muted>
          Adopts an existing slot, as the pipeline code says. Choose one of the
          above to replace it.
        </Muted>
      )}
      {error && <ErrorLine role="alert">{error}</ErrorLine>}
    </Panel>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${FORM.gapitems}px;
  padding: 12px 16px;
  border-bottom: 1px solid ${(p) => p.theme.semanticBorderLight};
  font-size: 13px;
`;

const Title = styled.div`
  font-weight: 600;
`;

const Indented = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 22px;
`;

const Muted = styled.span`
  opacity: 0.7;
`;

const ErrorLine = styled.div`
  color: ${(p) => p.theme.alertErrorText};
`;
